import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';
import {
	AppLauncherAuthMethod,
	GtmIdentityService,
} from './gtm-identity.service';

const TOKEN_KEY = 'app-launcher-auth-token';
const REFRESH_KEY = 'app-launcher-refresh-token';
const USER_KEY = 'app-launcher-user';
/** Shared with central-web-apps-wc embed metas. */
const WC_TOKEN_KEY = 'central-wc-embed-auth-token';

/**
 * Renew this long before `exp`. The embedded web component polls on a few-second cadence, so the
 * window only has to be wide enough to cover one refresh round trip plus a backgrounded tab.
 */
const REFRESH_SKEW_SECONDS = 300;

export interface BetaAuthUser {
	userId: string;
	email: string;
	authMethod: AppLauncherAuthMethod;
}

interface BetaAuthResponse {
	token: string;
	refresh_token: string;
	user_id: string;
	email: string;
	auth_method: string;
}

@Injectable({ providedIn: 'root' })
export class BetaAuthService {
	private token: string | null = null;
	private refreshToken: string | null = null;
	private user: BetaAuthUser | null = null;
	private refreshInFlight: Promise<BetaAuthUser> | null = null;
	private readonly tokenChanged = new Subject<string>();

	constructor(
		private readonly http: HttpClient,
		private readonly gtmIdentity: GtmIdentityService,
	) {
		this.restoreFromStorage();
	}

	/** Emits the new access token whenever it is replaced, so embedders can swap it in place. */
	public get tokenChanges(): Observable<string> {
		return this.tokenChanged.asObservable();
	}

	public getToken(): string | null {
		return this.token;
	}

	public getUser(): BetaAuthUser | null {
		return this.user;
	}

	public isAuthenticated(): boolean {
		return Boolean(this.token && this.user);
	}

	/**
	 * Restore session from sessionStorage. Returns true when a token+user exist.
	 * Pushes GTM identity when restore succeeds.
	 */
	public restoreSession(): boolean {
		this.restoreFromStorage();
		if (!this.isAuthenticated() || !this.user) {
			return false;
		}
		this.gtmIdentity.pushAppLauncherIdentityReady(this.user);
		return true;
	}

	public async register(email: string, password: string): Promise<BetaAuthUser> {
		const body = await this.postAuth('/register', { email, password });
		return this.applyAuthResponse(body);
	}

	public async login(email: string, password: string): Promise<BetaAuthUser> {
		const body = await this.postAuth('/login', { email, password });
		return this.applyAuthResponse(body);
	}

	public async forgotPassword(email: string): Promise<void> {
		await this.postJson('/forgot-password', { email });
	}

	public async resetPassword(token: string, password: string): Promise<void> {
		await this.postJson('/reset-password', { token, password });
	}

	public async providerAuth(idToken: string): Promise<BetaAuthUser> {
		const body = await this.postAuth('/provider-auth', {
			provider: 'firebase',
			idToken,
		});
		return this.applyAuthResponse(body);
	}

	/**
	 * Concurrent callers share one request — several pollers can fault at once, and each spawning its
	 * own refresh would rotate the refresh token out from under the others.
	 */
	public async refresh(): Promise<BetaAuthUser> {
		if (this.refreshInFlight) {
			return this.refreshInFlight;
		}
		if (!this.refreshToken) {
			throw new Error('No refresh token available.');
		}
		this.refreshInFlight = this.postAuth('/refresh', {
			refresh_token: this.refreshToken,
		})
			.then((body) => this.applyAuthResponse(body))
			.finally(() => {
				this.refreshInFlight = null;
			});
		return this.refreshInFlight;
	}

	/**
	 * Renews the access token when it is at or near `exp`. Safe to call often; it is a no-op while the
	 * current token still has room. Returns false when the session could not be kept alive.
	 */
	public async ensureFreshToken(): Promise<boolean> {
		if (!this.token) {
			return false;
		}
		if (!this.isExpiringSoon(this.token)) {
			return true;
		}
		if (!this.refreshToken) {
			return false;
		}
		try {
			await this.refresh();
			return true;
		} catch {
			return false;
		}
	}

	private isExpiringSoon(token: string): boolean {
		const exp = this.readExp(token);
		if (exp === null) {
			return false;
		}
		return exp - Date.now() / 1000 <= REFRESH_SKEW_SECONDS;
	}

	private readExp(token: string): number | null {
		const parts = token.split('.');
		if (parts.length !== 3) {
			return null;
		}
		try {
			const payload = JSON.parse(
				atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
			) as { exp?: unknown };
			return typeof payload.exp === 'number' ? payload.exp : null;
		} catch {
			return null;
		}
	}

	public async logout(): Promise<void> {
		try {
			if (this.apiBase()) {
				await firstValueFrom(
					this.http.post(`${this.apiBase()}/logout`, {}),
				);
			}
		} catch {
			// Stateless logout — still clear client session.
		}
		this.clearSession();
	}

	public clearSession(): void {
		this.token = null;
		this.refreshToken = null;
		this.user = null;
		try {
			sessionStorage.removeItem(TOKEN_KEY);
			sessionStorage.removeItem(REFRESH_KEY);
			sessionStorage.removeItem(USER_KEY);
			sessionStorage.removeItem(WC_TOKEN_KEY);
			sessionStorage.removeItem('central-wc-embed-account-resource-id');
		} catch {
			// ignore storage errors
		}
	}

	private apiBase(): string {
		return (environment.betaApiBase || '').trim().replace(/\/+$/, '');
	}

	private async postAuth(
		path: string,
		payload: Record<string, string>,
	): Promise<BetaAuthResponse> {
		return this.postJson<BetaAuthResponse>(path, payload);
	}

	private async postJson<T>(
		path: string,
		payload: Record<string, string>,
	): Promise<T> {
		const base = this.apiBase();
		if (!base) {
			throw new Error('betaApiBase is not configured.');
		}
		try {
			return await firstValueFrom(
				this.http.post<T>(`${base}${path}`, payload),
			);
		} catch (err) {
			throw this.toError(err);
		}
	}

	private applyAuthResponse(body: BetaAuthResponse): BetaAuthUser {
		const user: BetaAuthUser = {
			userId: body.user_id,
			email: body.email,
			authMethod: this.mapAuthMethod(body.auth_method),
		};
		this.token = body.token;
		this.refreshToken = body.refresh_token;
		this.user = user;
		this.persist();
		this.gtmIdentity.pushAppLauncherIdentityReady(user);
		this.tokenChanged.next(body.token);
		return user;
	}

	private mapAuthMethod(raw: string): AppLauncherAuthMethod {
		const v = (raw || '').toLowerCase();
		if (v === 'google' || v === 'firebase') {
			return 'google';
		}
		return 'email';
	}

	private persist(): void {
		try {
			if (this.token) {
				sessionStorage.setItem(TOKEN_KEY, this.token);
				sessionStorage.setItem(WC_TOKEN_KEY, this.token);
			}
			if (this.refreshToken) {
				sessionStorage.setItem(REFRESH_KEY, this.refreshToken);
			}
			if (this.user) {
				sessionStorage.setItem(USER_KEY, JSON.stringify(this.user));
			}
		} catch {
			// ignore storage errors
		}
	}

	private restoreFromStorage(): void {
		try {
			const token = sessionStorage.getItem(TOKEN_KEY);
			const refresh = sessionStorage.getItem(REFRESH_KEY);
			const rawUser = sessionStorage.getItem(USER_KEY);
			if (!token || !rawUser) {
				return;
			}
			const parsed = JSON.parse(rawUser) as BetaAuthUser;
			if (!parsed?.userId || !parsed?.email) {
				return;
			}
			this.token = token;
			this.refreshToken = refresh;
			this.user = {
				userId: parsed.userId,
				email: parsed.email,
				authMethod: parsed.authMethod === 'google' ? 'google' : 'email',
			};
			sessionStorage.setItem(WC_TOKEN_KEY, token);
		} catch {
			this.clearSession();
		}
	}

	private toError(err: unknown): Error {
		if (err instanceof HttpErrorResponse) {
			const msg =
				(err.error &&
					typeof err.error === 'object' &&
					'message' in err.error &&
					typeof (err.error as { message: unknown }).message ===
						'string' &&
					(err.error as { message: string }).message) ||
				err.message ||
				'Authentication failed.';
			return new Error(msg);
		}
		if (err instanceof Error) {
			return err;
		}
		return new Error('Authentication failed.');
	}
}
